import axios from 'axios'
import { useAuthStore } from '@/stores/authStore'
import router from '@/router'

const DEVICE_ID_KEY = 'deviceId'

// crypto.randomUUID() only exists in secure contexts (HTTPS or localhost) -
// fall back to crypto.getRandomValues (allowed in insecure contexts too),
// then to Math.random as a last resort for very old browsers.
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function getOrCreateDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY)

  if (!id) {
    id = generateUUID()
    localStorage.setItem(DEVICE_ID_KEY, id)
  }

  return id
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL || 'http://localhost:3000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// REQUEST
apiClient.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    const token = authStore.token
    const isAdminRoute = window.location.pathname.startsWith('/admin')

    if (token && isAdminRoute) {
      config.headers.Authorization = `Bearer ${token}`
    }

    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone
    config.headers['X-Device-Id'] = getOrCreateDeviceId()

    return config
  },
  (error) => Promise.reject(error)
)

// RESPONSE
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status

    if (!status) {
      return Promise.reject(error)
    }

    const authStore = useAuthStore()
    const currentRoute = router.currentRoute.value
    const isAdminRoute = currentRoute.path.startsWith('/admin')

    if (status === 401 && isAdminRoute) {
      authStore.clearAuthData()

      if (currentRoute.name !== 'LoginPage') {
        sessionStorage.setItem('loginMessage', 'Your session has expired. Please log in again.')

        setTimeout(() => {
          router.replace({
            name: 'LoginPage',
            query: { redirect: currentRoute.fullPath },
          })
        }, 0)
      }
    }

    if (status === 403 && isAdminRoute) {
      setTimeout(() => router.replace({ name: 'ForbiddenPage' }), 0)
    }

    console.error('API Error:', error.response?.data || error.message)
    return Promise.reject(error)
  }
)

export default apiClient
